import { AccountMembershipResource } from '@mkp/account-membership/data-access';
import { Account, AccountDto } from '@mkp/account/models';
import {
  LegalEntity,
  LegalEntityResource,
  mapLegalEntityDtosToLegalEntities,
} from '@mkp/legal-entity/data-access';
import { isRevisionByClient, RevisionBy, RevisionByClientName } from '@mkp/shared/models';
import { QueryParams } from '@mkp/shared/data-access';
import { map, Observable, of, OperatorFunction, pipe, switchMap, take } from 'rxjs';
import { mapAccountDtoToAccount } from '../account.mapper';

export const filterAccountsWithUserMemberships = (
  accountMembershipResource: AccountMembershipResource,
  loggedInUserId: string | undefined
): OperatorFunction<AccountDto[], AccountDto[]> =>
  pipe(
    switchMap((accounts: AccountDto[]) => {
      if (!accounts || accounts.length === 0) {
        return of([]);
      }
      // this could be onboarding recovery case
      if (accounts.length === 1) {
        return of(accounts);
      }

      return accountMembershipResource
        .getWithQuery({
          filter: `user.id==${loggedInUserId};${accounts.map((account) => `account.id==${account.id}`).join(',')}`,
          limit: 200,
        })
        .pipe(
          take(1),
          map(({ _embedded }) => _embedded?.results || []),
          map((memberships) => memberships.map(({ accountId }) => accountId)),
          map((accountIds) => accounts.filter((account) => accountIds.includes(account.id)))
        );
    })
  );

export const addLegalEntityToAccountDto = (
  legalEntityResource: LegalEntityResource
): OperatorFunction<AccountDto, Account> =>
  pipe(
    switchMap((account: AccountDto) =>
      fetchLegalEntitiesAndMapToAccount([account], legalEntityResource)
    ),
    map((accounts) => accounts[0])
  );

export const isAccount = (account: unknown): account is Account =>
  account != null &&
  (account as Account).id !== undefined &&
  (account as Account).legalEntity !== undefined;

export const addLegalEntityToAccountsDto = (
  legalEntityResource: LegalEntityResource
): OperatorFunction<AccountDto[], Account[]> =>
  pipe(
    switchMap((accounts: AccountDto[]) =>
      fetchLegalEntitiesAndMapToAccount(accounts, legalEntityResource)
    ),
    map((accounts) => accounts.filter(isAccount))
  );

const fetchLegalEntitiesAndMapToAccount = (
  accounts: AccountDto[],
  legalEntityResource: LegalEntityResource
): Observable<Account[]> =>
  legalEntityResource.getWithQuery(getLegalEntityFilter(accounts)).pipe(
    map(({ _embedded }) => _embedded?.results ?? []),
    map(mapLegalEntityDtosToLegalEntities),
    map((legalEntities) => mapAccountDtosToAccounts(accounts, legalEntities)),
    take(1)
  );

export const mapAccountDtosToAccounts = (
  accounts: AccountDto[],
  legalEntities: LegalEntity[]
): Account[] => {
  const legalEntityMap = new Map(legalEntities.map((legalEntity) => [legalEntity.id, legalEntity]));
  return accounts.map((account) =>
    mapAccountDtoToAccount(account, legalEntityMap.get(account.legalEntityId) as LegalEntity)
  );
};

const getLegalEntityFilter = (accounts: AccountDto[]): QueryParams => {
  const uniqueLegalEntityIds = [...new Set(accounts.map((account) => account.legalEntityId))];
  return uniqueLegalEntityIds.length
    ? {
        filter: uniqueLegalEntityIds.map((legalEntityId) => `id==${legalEntityId}`).join(','),
        limit: uniqueLegalEntityIds.length,
      }
    : {};
};

export const isTechnicalWorkspace = (
  account: Account
): account is Account & {
  createdBy: RevisionBy & { _type: 'client' };
} => {
  const createdBy = account.createdBy;
  return (
    createdBy &&
    isRevisionByClient(createdBy) &&
    Object.values(RevisionByClientName).includes(createdBy.name)
  );
};

export const isRevisionByClientName = (
  account: Account,
  revisionByClientName: RevisionByClientName
): account is Account & {
  createdBy: RevisionBy & { _type: 'client' };
} => {
  const createdBy = account.createdBy;
  return createdBy && isRevisionByClient(createdBy) && createdBy.name === revisionByClientName;
};
